// Layer 3 (Transformations) for ProjectTasksScene — column grouping, due hints,
// and list-view projection. No React, no engine mutations.

import {
  PRIORITY_COLOR,
  type ProjectTask,
  type TaskColumn,
  type TaskPriority,
} from '@/versions/v1/shared/project-modules-seed';
import type { ProjectColor } from '@/versions/v1/shared/project-colors';

export const TASK_COLUMNS: { id: TaskColumn; label: string }[] = [
  { id: 'todo', label: 'To do' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'done', label: 'Done' },
];

export function priorityColor(priority: TaskPriority): ProjectColor {
  return PRIORITY_COLOR[priority];
}

export function priorityLabel(priority: TaskPriority): string {
  if (priority === 'high') return 'High';
  if (priority === 'medium') return 'Medium';
  return 'Low';
}

export function formatDueHint(dueAt: number | null, now: number): string {
  if (dueAt === null) return 'No due date';
  const diff = dueAt - now;
  const days = Math.ceil(diff / (24 * 60 * 60 * 1000));
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days < 7) return `Due in ${days}d`;
  return `Due ${new Date(dueAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })}`;
}

export function groupTasksByColumn(tasks: ProjectTask[]): Record<TaskColumn, ProjectTask[]> {
  return {
    todo: tasks.filter((t) => t.column === 'todo'),
    in_progress: tasks.filter((t) => t.column === 'in_progress'),
    done: tasks.filter((t) => t.column === 'done'),
  };
}

export function sortTasksForList(tasks: ProjectTask[]): ProjectTask[] {
  const order: Record<TaskColumn, number> = { todo: 0, in_progress: 1, done: 2 };
  const priorityOrder: Record<TaskPriority, number> = { high: 0, medium: 1, low: 2 };
  return [...tasks].sort((a, b) => {
    const col = order[a.column] - order[b.column];
    if (col !== 0) return col;
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });
}

export function nextTaskId(tasks: ProjectTask[]): string {
  const max = tasks.reduce((acc, task) => {
    const n = Number.parseInt(task.id.replace(/\D/g, ''), 10);
    return Number.isFinite(n) ? Math.max(acc, n) : acc;
  }, 0);
  return `task-new-${max + 1}`;
}

export function columnLabel(column: TaskColumn): string {
  return TASK_COLUMNS.find((c) => c.id === column)?.label ?? column;
}
